import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle, Clock, EnvelopeSimple, ListChecks, Receipt, Ticket } from '@phosphor-icons/react';
import { supabase } from '../../lib/supabase/client';
import { baueArbeitsvorschlag, type VorschlagEintrag } from '../../lib/dashboard/arbeitsvorschlag';
import type { BriefingMail } from '../../lib/dashboard/briefing';
import { ladeSichtbareRechnungen, type SichtRechnung } from '../../lib/dashboard/rechnungenSicht';
import { istMeinBeleg, istMeineZustaendigkeit, type DashboardScope, type ScopeNutzer } from '../../lib/dashboard/scope';
import { DashboardWidget, KopfInfo, WidgetKopf, WidgetLaedt, WidgetLeer } from './DashboardWidget';

interface Props {
  mails: BriefingMail[];
  onOpenEmail: (id: string) => void;
  onNavigateToTicket?: (id: string) => void;
  onNavigateToRechnung?: (id: string) => void;
  userProfile?: any;
  scope: DashboardScope;
  nutzer: ScopeNutzer;
}

interface RohTicket {
  id: string; titel: string; status: string; prioritaet: string | null; faellig_am: string | null;
  zustaendig_id: string | null; zustaendig_typ: string | null;
}

const QUELLE_ICON = { mail: EnvelopeSimple, ticket: Ticket, rechnung: Receipt } as const;
const QUELLE_NAME = { mail: 'E-Mail', ticket: 'Ticket', rechnung: 'Rechnung' } as const;

/** Icon-Farbe nach Dringlichkeit wie bisher: ab 100 rot, ab 70 orange, sonst neutral. */
const tonNachScore = (score: number) =>
  score >= 100 ? { bg: 'var(--bad-soft)', fg: 'var(--bad)' }
    : score >= 70 ? { bg: 'var(--warn-soft)', fg: 'var(--warn)' }
      : { bg: 'var(--fill2)', fg: 'var(--ink3)' };

const heuteIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const ArbeitsvorschlagWidget: React.FC<Props> = ({ mails, onOpenEmail, onNavigateToTicket, onNavigateToRechnung, userProfile, scope, nutzer }) => {
  // Rohe (ungefilterte) Listen im State — die Scope-Filterung passiert unten in useMemo,
  // damit das Umschalten Meine/Alles ohne erneuten Fetch neu rechnet.
  const [rohTickets, setRohTickets] = useState<RohTicket[]>([]);
  const [rohRechnungen, setRohRechnungen] = useState<SichtRechnung[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [tickets, sicht] = await Promise.all([
        supabase.from('tickets')
          .select('id, titel, status, prioritaet, faellig_am, zustaendig_id, zustaendig_typ')
          .not('status', 'in', '(erledigt,geschlossen)')
          .is('geloescht_am', null)
          .limit(100),
        // Sichtbarkeitsgefiltert wie die Rechnungsliste (personen-/team-orientiert) —
        // sonst würden Team-Nutzer Rechnungen als Tagesaufgabe vorgeschlagen bekommen,
        // die die Finanz-Karten/Rechnungsliste für sie ausblenden.
        ladeSichtbareRechnungen(userProfile),
      ]);
      if (cancelled) return;
      const rechnungen = sicht.rechnungen.filter(r => ['offen', 'klaerung', 'unklar'].includes((r.status || '').toLowerCase()));
      const ticketsGemappt = ((tickets.data || []) as any[]).map((t) => ({
        id: t.id, titel: t.titel, status: t.status, prioritaet: t.prioritaet, faellig_am: t.faellig_am,
        zustaendig_id: t.zustaendig_id ?? null, zustaendig_typ: t.zustaendig_typ ?? null,
      }));
      setRohTickets(ticketsGemappt);
      setRohRechnungen(rechnungen);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [userProfile?.id]);

  // Scope-Filterung + Vorschlag-Berechnung: reine Neuberechnung aus den rohen Listen,
  // kein Fetch — läuft bei jedem Umschalten Meine/Alles neu.
  const eintraege = useMemo(() => {
    const sichtbareMails = scope === 'alle' ? mails
      : mails.filter((m) => istMeineZustaendigkeit(m.eff_zustaendig_id, m.eff_zustaendig_typ, nutzer));
    const sichtbareTickets = scope === 'alle' ? rohTickets
      : rohTickets.filter((t) => istMeineZustaendigkeit(t.zustaendig_id, t.zustaendig_typ, nutzer));
    const sichtbareRechnungen = scope === 'alle' ? rohRechnungen
      : rohRechnungen.filter((r) => istMeinBeleg(r, nutzer));
    return baueArbeitsvorschlag({
      mails: sichtbareMails,
      tickets: sichtbareTickets,
      rechnungen: sichtbareRechnungen.map((r) => ({
        id: r.id, status: r.status || '', faelligkeitsdatum: r.faelligkeitsdatum,
        titel: `Rechnung ${r.rechnungsnummer || ''} ${r.unternehmen || ''}`.trim(),
      })),
      heuteIso: heuteIso(),
    });
  }, [mails, rohTickets, rohRechnungen, scope, nutzer]);

  const oeffne = (e: VorschlagEintrag) => {
    if (e.quelle === 'mail') onOpenEmail(e.id);
    else if (e.quelle === 'ticket') onNavigateToTicket?.(e.id);
    else onNavigateToRechnung?.(e.id);
  };

  const gesamtMin = eintraege.reduce((s, e) => s + e.minuten, 0);

  return (
    <DashboardWidget farbe="--dw-blau" icon={ListChecks} ariaLabel="Arbeitsvorschlag des Tages">
      <WidgetKopf farbe="--dw-blau" icon={ListChecks} titel="Arbeitsvorschlag des Tages"
        untertitel={`${eintraege.length} Aufgaben · ~${gesamtMin} Min.`}
        rechts={<KopfInfo tip="Arbeitsvorschlag – Mails, Tickets und Rechnungen nach Priorität und Frist sortiert, höchstens 10" />} />
      {loading ? (
        <WidgetLaedt text="Lade…" />
      ) : eintraege.length === 0 ? (
        <WidgetLeer icon={CheckCircle} text="Alles abgearbeitet" farbeIcon="var(--ok)" />
      ) : (
        <ol className="m-0 flex list-none flex-col p-0" style={{ gap: 2 }}>
          {eintraege.map((e, i) => {
            const Icon = QUELLE_ICON[e.quelle];
            const ton = tonNachScore(e.score);
            return (
              <li key={`${e.quelle}-${e.id}`}>
                <button type="button" onClick={() => oeffne(e)} data-tip={`${QUELLE_NAME[e.quelle]} öffnen – ${e.titel}`}
                  className="dw-zeile flex w-full items-center border-0 bg-transparent text-left"
                  style={{ gap: 10, minHeight: 36, padding: '6px 10px', borderRadius: 12, fontFamily: 'inherit', cursor: 'pointer' }}>
                  <span className="shrink-0 tabular-nums" style={{ width: 20, fontSize: 12, fontWeight: 800, color: 'var(--ink3)' }}>{i + 1}.</span>
                  <span className="flex shrink-0 items-center justify-center" style={{ width: 28, height: 28, borderRadius: 9, background: ton.bg, color: ton.fg }}>
                    <Icon weight="fill" size={15} />
                  </span>
                  <span className="min-w-0 flex-1 truncate" style={{ fontSize: 13.5, fontWeight: 600 }}>{e.titel}</span>
                  <span className="flex shrink-0 items-center tabular-nums" style={{ gap: 4, fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>
                    <Clock weight="bold" size={12} /> {e.minuten} Min.
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      )}
    </DashboardWidget>
  );
};

export default ArbeitsvorschlagWidget;
